import type { ShopifyOfferDto } from './offer.types.ts';
import type { CouponDto } from './services/checkout/checkout.types.ts';

interface CommonSectionData {}

export interface UserIdentificationData extends CommonSectionData {
  fullName: string;
  email: string;
  cpfOnIdentification: boolean; // Tema: !requireCpfOnlyAtPayment
  cpf?: string; // CPF is a Brazilian individual taxpayer registry identification
  phone: string;
  birth?: string; // Data de nascimento no formato DD/MM/AAAA (será convertida para YYYY-MM-DD)
  /** Tema `store_checkouts.steps.identification.requestBirthDate` */
  requestBirthDate?: boolean;
}

export interface ShippingAddressData extends CommonSectionData {
  zipCode: string;
  street: string;
  number: string;
  complement?: string;
  neighborhood: string;
  city: string;
  state: string;
}

// @ts-ignore
export enum PaymentMethod {
  CREDIT_CARD = 'credit_card',
  PIX = 'pix',
  BOLETO = 'boleto',
  APPLE_PAY = 'apple_pay',
  GOOGLE_PAY = 'google_pay',
}

export interface CreditCardData {
  cardNumber: string;
  expirationDate: string; // MM/YY
  cvv: string;
  cardholderName: string;
  cpf?: string; // ✅ OPCIONAL: CPF agora vem do cliente (cartData.customerData.document)
  installments: string; // e.g., "1", "2", ...
}

export interface PixData {
  cpf: string;
}

export interface BoletoData {
  cpf: string;
}

export interface PaymentInformationData extends CommonSectionData {
  paymentMethod: PaymentMethod | ''; // Allow empty string for initial state before selection
  creditCard?: CreditCardData;
  pix?: PixData;
  boleto?: BoletoData;
}

// ✅ NOVA INTERFACE: Dados de pagamento para finalização (sem depender do formData)
export interface FinalPaymentData {
  paymentMethod: PaymentMethod;
  paymentIntentClientSecret?: string;
  // Dados do cartão (se for cartão)
  creditCard?: {
    cardNumber: string;
    expirationDate: string;
    cvv: string;
    cardholderName: string;
    cpf: string;
    installments: string;
  };

  // Dados do PIX (se for PIX)
  pix?: {
    cpf: string;
  };

  // Dados do boleto (se for boleto)
  boleto?: {
    cpf: string;
  };
}

export interface Product {
  id: string | number;
  name: string;
  price: number;
  quantity: number;
  totalPrice: number; // ✅ Obrigatório - sempre calculado
  imageUrl?: string;
  variant_id?: string | number;
  properties?: Record<string, string>;
}

export interface OrderSummaryData {
  products: Product[];
  subtotal: number;
  shipping: number | 'Grátis'; // Can be a number or the string "Grátis"
  total: number;
  couponCode?: string;
  coupons: CouponDto[]; // Optional: to display the coupon description
  discountAmount?: number; // Optional: to display the actual discount value
}

export interface FormData {
  userIdentification: UserIdentificationData;
  shippingAddress: ShippingAddressData;
  paymentInformation: PaymentInformationData;
  orderSummary: OrderSummaryData;
  shopifyOffer: ShopifyOfferDto;
}

export class StepperStep {
  readonly #id: string;
  readonly #name: string;
  #completed: boolean = false;

  constructor(id: string, name: string) {
    this.#id = id;
    this.#name = name;
  }

  get id(): string {
    return this.#id;
  }

  get name(): string {
    return this.#name;
  }

  get status(): 'completed' | 'pending' {
    return this.#completed ? 'completed' : 'pending';
  }

  isCompleted(formData: FormData): boolean {
    const isIdentificationCompleted = (userIdentification: UserIdentificationData): boolean => {
      // Validação inline para evitar dependência circular
      const requiredFields = [userIdentification.fullName, userIdentification.email, userIdentification.phone];
      const hasRequiredFields = requiredFields.every((field) => Boolean(field?.trim()));

      if (userIdentification.cpfOnIdentification && !userIdentification.cpf?.trim()) {
        return false;
      }

      return hasRequiredFields;
    };

    const isShippingAddressCompleted = (shippingAddress: ShippingAddressData): boolean => {
      const fields = [
        shippingAddress.zipCode,
        shippingAddress.street,
        shippingAddress.number,
        shippingAddress.neighborhood,
        shippingAddress.city,
        shippingAddress.state,
      ];
      return fields.filter(Boolean).map((field) => String(field).trim()).length === fields.length;
    };

    const isPaymentCompleted = (paymentInformation: PaymentInformationData): boolean => {
      switch (paymentInformation.paymentMethod) {
        case PaymentMethod.CREDIT_CARD: {
          const fields = [
            paymentInformation.creditCard?.cardNumber,
            paymentInformation.creditCard?.expirationDate,
            paymentInformation.creditCard?.cvv,
            paymentInformation.creditCard?.cardholderName,
            paymentInformation.creditCard?.cpf,
            paymentInformation.creditCard?.installments,
          ];
          const validFields = fields.filter(Boolean).map((field) => String(field).trim()).length;
          const isComplete = validFields === fields.length;
          return isComplete;
        }

        case PaymentMethod.PIX: {
          const isComplete = Boolean(paymentInformation.pix?.cpf?.trim());
          return isComplete;
        }

        case PaymentMethod.BOLETO: {
          const isComplete = Boolean(paymentInformation.boleto?.cpf?.trim());
          return isComplete;
        }

        case PaymentMethod.APPLE_PAY:
        case PaymentMethod.GOOGLE_PAY:
          return true;

        default:
          return false;
      }
    };

    switch (this.#id) {
      case 'IDENTIFICATION':
        return isIdentificationCompleted(formData.userIdentification);

      case 'SHIPPING':
        return isShippingAddressCompleted(formData.shippingAddress);

      case 'PAYMENT':
        return isPaymentCompleted(formData.paymentInformation);

      // 'revisao' step is considered completed if all others are.
      // Or, it's always 'pending' until order is placed, depending on logic.
      // For stepper display, it's often implicitly completed if prior steps are.
      // The `updateStatus` will rely on this logic.
      default: // This case should ideally not be hit if IDs are specific.
        // However, as a fallback for a 'revisao' step or similar:
        return (
          isIdentificationCompleted(formData.userIdentification) &&
          isShippingAddressCompleted(formData.shippingAddress) &&
          isPaymentCompleted(formData.paymentInformation)
        );
    }
  }

  /***
   * Updates the step's completion status based on the provided form data.
   */
  public updateStatus(formData: FormData): boolean {
    const completed = this.isCompleted(formData);
    if (this.#completed !== completed) {
      this.#completed = completed;
      return true; // Status changed
    }
    return false; // Status remains the same
  }
}

export type EditingSection = 'IDENTIFICATION' | 'SHIPPING' | 'PAYMENT' | null;
